/**
 * Sign in with Apple (native iOS). Uses the system dialog via
 * @capacitor-community/apple-sign-in and returns the `identityToken` (a JWT
 * signed by Apple). The token is posted to the server which verifies it against
 * Apple's public keys and issues our own session token.
 *
 * Apple only returns the user's name on the FIRST authorization, so we forward
 * it when present; subsequent logins rely on the stored display name.
 */
import { Capacitor } from '@capacitor/core';

export type AppleResult =
  | { ok: true; identityToken: string; fullName?: string; email?: string }
  | { ok: false; error: string };

// The iOS bundle id Apple stamps as the token audience. Keep in sync with the
// server's APPLE_BUNDLE_ID env (defaults to the same value).
const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID ?? 'games.dontmiss.app';

export function appleSignInSupported(): boolean {
  return Capacitor.getPlatform() === 'ios';
}

export async function startAppleSignIn(): Promise<AppleResult> {
  if (!appleSignInSupported()) {
    return { ok: false, error: 'Sign in with Apple is only available on iOS.' };
  }
  try {
    const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
    const res = await SignInWithApple.authorize({
      clientId: APPLE_CLIENT_ID,
      redirectURI: '',
      scopes: 'email name',
    });
    const r = res.response;
    if (!r?.identityToken) return { ok: false, error: 'No identity token returned.' };

    const fullName = [r.givenName, r.familyName].filter(Boolean).join(' ').trim() || undefined;
    return {
      ok: true,
      identityToken: r.identityToken,
      fullName,
      email: r.email ?? undefined,
    };
  } catch (e: unknown) {
    // User-cancelled cancels are surfaced as a generic error; the caller decides
    // whether to show a toast.
    const msg = e instanceof Error ? e.message : String(e);
    return { ok: false, error: msg };
  }
}
